import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PhoneCall, ArrowRight } from "@phosphor-icons/react";

const HERO_STYLE = {
    backgroundColor: "#141210",
    backgroundImage: `
        radial-gradient(circle at 80% 15%, rgba(255,90,54,0.24), transparent 45%),
        radial-gradient(circle at 10% 90%, rgba(255,90,54,0.10), transparent 40%),
        linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)
    `,
    backgroundSize: "100% 100%, 100% 100%, 46px 46px, 46px 46px",
};

export default function Register() {
    const { user, register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "" });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await register(form);
            navigate("/");
        } catch (e) {
            setErr(e.message);
        } finally {
            setBusy(false);
        }
    };

    const inputCls = "mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none transition-all placeholder:text-stone-400 focus:border-brand focus:ring-4 focus:ring-brand/15";

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
            {/* Left: dark textured hero */}
            <div className="relative hidden flex-col justify-between overflow-hidden p-12 lg:flex xl:p-16" style={HERO_STYLE} data-testid="auth-hero">
                <div className="grain pointer-events-none absolute inset-0 opacity-40" />
                <div className="relative flex items-center gap-2.5">
                    <img src="/inspiro-connect-mark.png" alt="Inspiro Connect" className="h-10 w-10 object-contain" />
                    <div>
                        <div className="font-heading text-lg font-bold text-white">Inspiro Connect</div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50">Omnichannel, unified, and intelligence</div>
                    </div>
                </div>

                <div className="relative max-w-lg">
                    <h2 className="font-heading text-5xl font-extrabold leading-[1.03] tracking-tight text-white xl:text-6xl">
                        Launch your<br />call center in<br /><span className="text-brand">minutes.</span>
                    </h2>
                    <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
                        Create a workspace, connect your telephony provider, and start dialing — no hardware, no lock-in.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">
                        <span>Free to start</span>
                        <span>Provider-agnostic</span>
                        <span>Secure by design</span>
                    </div>
                </div>

                <div className="relative" aria-hidden />
            </div>

            {/* Right: form */}
            <div className="flex flex-col justify-center bg-paper px-8 py-12 sm:px-16 lg:px-24">
                <div className="mx-auto w-full max-w-sm">
                    <div className="mb-10 flex lg:hidden">
                        <img src="/inspiro-connect-logo.png" alt="Inspiro Connect" className="h-9 w-auto object-contain" />
                    </div>

                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Create workspace</div>
                    <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Get started</h1>
                    <p className="mt-2 text-sm text-stone2">You'll be the workspace admin. Add agents later from Settings.</p>

                    <form onSubmit={submit} className="mt-9 space-y-5" data-testid="register-form">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Your name</label>
                                <input data-testid="register-name-input" required value={form.name} onChange={onChange("name")} className={inputCls} placeholder="Jane Doe" />
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Workspace</label>
                                <input data-testid="register-org-input" required value={form.org_name} onChange={onChange("org_name")} className={inputCls} placeholder="Acme Inc" />
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Email</label>
                            <input data-testid="register-email-input" type="email" required value={form.email} onChange={onChange("email")} className={inputCls} placeholder="you@company.com" />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Password</label>
                            <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={onChange("password")} className={inputCls} placeholder="At least 6 characters" />
                        </div>
                        {err && (
                            <div data-testid="register-error" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                                {err}
                            </div>
                        )}
                        <button
                            data-testid="register-submit-button"
                            disabled={busy}
                            className="group flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 font-heading text-base font-semibold text-white shadow-lg shadow-brand/25 transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-50"
                        >
                            {busy ? "Creating…" : "Create Workspace"}
                            <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </form>

                    <p className="mt-8 text-sm text-stone2">
                        Already have an account?{" "}
                        <Link to="/login" data-testid="go-login-link" className="font-semibold text-ink underline-offset-4 hover:text-brand hover:underline">
                            Sign in →
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
